'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import Container from '@/components/ui/Container'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { useToast } from '@/components/ui/ToastContainer'

interface Event {
  id: string
  name: string
  description: string | null
  startTime: string
  endTime: string
  isCompleted: boolean
  isPublic: boolean
  isPairsEvent?: boolean
  club: {
    id: string
    name: string
    isPublic: boolean
  }
  eventType: {
    id: string
    name: string
  }
  scoreSheet: {
    name: string
  } | null
  _count: {
    participants: number
  }
  accessLevel: {
    canMakePublic: boolean
    isPublic: boolean
    isClubAdmin: boolean
    isEventAdmin: boolean
    isSuperAdmin: boolean
  }
}

export default function PublicEventsPage() {
  const { data: session } = useSession()
  const { showToast } = useToast()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'active' | 'completed'>('all')
  const [updatingEvents, setUpdatingEvents] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadEvents()
  }, [session]) // Reload when session changes

  async function loadEvents() {
    try {
      // Always use accessible events API (handles both authenticated and guest users)
      const response = await fetch('/api/events/accessible')
      if (response.ok) {
        const data = await response.json()
        setEvents(data)
      }
    } catch (error) {
      console.error('Error loading events:', error)
    } finally {
      setLoading(false)
    }
  }

  async function toggleEventPublic(eventId: string, currentStatus: boolean) {
    setUpdatingEvents(prev => new Set(prev).add(eventId))
    try {
      const response = await fetch(`/api/events/${eventId}/public`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: !currentStatus })
      })

      if (response.ok) {
        const data = await response.json()
        showToast({
          type: 'success',
          title: 'Event Updated',
          message: data.message
        })
        await loadEvents() // Reload events to get updated data
      } else {
        const data = await response.json()
        const statusCode = response.status
        const errorMessage = data.error || 'Failed to update event'
        const detailedError = `Status: ${statusCode} - ${errorMessage}${data.details ? ` (${data.details})` : ''}`
        
        console.error('Toggle event public error:', {
          status: statusCode,
          error: data.error,
          details: data.details,
          fullResponse: data
        })
        
        showToast({
          type: 'error',
          title: 'Failed to Update Event',
          message: detailedError,
          duration: 8000,
        })
      }
    } catch (error) {
      console.error('Toggle event public error:', error)
      const errorMessage = `Network/Client Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      showToast({
        type: 'error',
        title: 'Failed to Update Event',
        message: errorMessage,
        duration: 8000,
      })
    } finally {
      setUpdatingEvents(prev => {
        const newSet = new Set(prev)
        newSet.delete(eventId)
        return newSet
      })
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  function formatDuration(start: string, end: string) {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const diffMs = endDate.getTime() - startDate.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    const remainingHours = diffHours % 24

    if (diffDays > 0) {
      return diffDays === 1 
        ? `1 day ${remainingHours} hours`
        : `${diffDays} days ${remainingHours} hours`
    }
    return `${diffHours} hours`
  }

  function getEventStatus(event: Event): 'upcoming' | 'active' | 'completed' {
    const now = new Date()
    const start = new Date(event.startTime)
    const end = new Date(event.endTime)

    if (event.isCompleted) return 'completed'
    if (now < start) return 'upcoming'
    if (now >= start && now <= end) return 'active'
    return 'completed'
  }

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true
    return getEventStatus(event) === filter
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading events...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-ocean-600 to-teal-600 text-white shadow-lg">
        <Container className="py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">📅 Fishing Events</h1>
            <p className="text-ocean-100 text-lg mb-6">
              Browse public fishing events from clubs across the region
            </p>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-3 justify-center mb-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-white text-ocean-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                All Events ({events.length})
              </button>
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'upcoming'
                    ? 'bg-white text-ocean-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Upcoming ({events.filter(e => getEventStatus(e) === 'upcoming').length})
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'active'
                    ? 'bg-white text-ocean-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Active ({events.filter(e => getEventStatus(e) === 'active').length})
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'completed'
                    ? 'bg-white text-ocean-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Completed ({events.filter(e => getEventStatus(e) === 'completed').length})
              </button>
            </div>
            
            {/* Sign In Button for Guests */}
            {!session && (
              <div className="mt-4">
                <Button href="/auth/signin" variant="secondary" size="sm">
                  Sign In to Access More Events
                </Button>
              </div>
            )}
          </div>
        </Container>
      </div>

      <Container className="py-8">
        {filteredEvents.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎣</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-600 mb-6">
                {filter === 'all'
                  ? 'There are no public events at the moment. Check back later!'
                  : `No ${filter} events to display.`}
              </p>
              {filter !== 'all' && (
                <Button onClick={() => setFilter('all')} variant="secondary">
                  View All Events
                </Button>
              )}
              {!session && (
                <div className="mt-4">
                  <Button href="/auth/signin" variant="secondary">
                    Sign In to Access More Events
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => {
              const status = getEventStatus(event)
              const statusColors = {
                upcoming: 'bg-blue-100 text-blue-800',
                active: 'bg-green-100 text-green-800',
                completed: 'bg-gray-100 text-gray-800'
              }
              const statusLabels = {
                upcoming: '🗓️ Upcoming',
                active: '🟢 Active Now',
                completed: '✅ Completed'
              }

              return (
                <Card key={event.id} className="hover:shadow-lg transition-shadow">
                  <div className="space-y-3">
                    {/* Header */}
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                          {event.name}
                        </h3>
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${statusColors[status]}`}>
                            {statusLabels[status]}
                          </span>
                          {!event.accessLevel?.isPublic && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap bg-orange-100 text-orange-800">
                              🔒 Private
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">
                          🏆 {event.club.name}
                          {!event.club.isPublic && (
                            <span className="ml-2 px-1 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                              🔒
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    {event.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {event.description}
                      </p>
                    )}

                    {/* Event Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs font-semibold rounded">
                          {event.eventType.name}
                        </span>
                        {event.scoreSheet && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                            🐟 Scored
                          </span>
                        )}
                      </div>
                      
                      <div className="text-gray-600 space-y-1">
                        <p>
                          <strong>Starts:</strong> {formatDate(event.startTime)}
                        </p>
                        <p>
                          <strong>Duration:</strong> {formatDuration(event.startTime, event.endTime)}
                        </p>
                        <p>
                          <strong>Participants:</strong> {event._count.participants}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-3 border-t border-gray-200 space-y-2">
                      {event.isPairsEvent && status === 'active' && (
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            href={`/admin/clubs/${event.club.id}/events/${event.id}`}
                            size="sm"
                            variant="secondary"
                          >
                            Pairs Sign-On
                          </Button>
                          <Button
                            href={`/admin/clubs/${event.club.id}/events/${event.id}/catches`}
                            size="sm"
                            variant="secondary"
                          >
                            Weigh-Ins
                          </Button>
                          <Button
                            href={`/admin/clubs/${event.club.id}/events/${event.id}/leaderboard`}
                            size="sm"
                            variant="secondary"
                          >
                            Leaderboard
                          </Button>
                          <Button
                            href={`/admin/clubs/${event.club.id}/events/${event.id}/spin-wheel`}
                            size="sm"
                            variant="secondary"
                          >
                            Spin The Wheel
                          </Button>
                        </div>
                      )}
                      <Button
                        href={`/events/${event.id}`}
                        size="sm"
                        className="w-full"
                        variant="secondary"
                      >
                        View Details →
                      </Button>
                      {session && event.accessLevel?.canMakePublic && (
                        <Button
                          onClick={() => toggleEventPublic(event.id, event.accessLevel.isPublic)}
                          size="sm"
                          className={`w-full ${
                            event.accessLevel.isPublic 
                              ? 'bg-gray-600 hover:bg-gray-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                          disabled={updatingEvents.has(event.id)}
                        >
                          {updatingEvents.has(event.id) ? 'Updating...' : 
                           event.accessLevel.isPublic ? 'Make Private' : 'Make Public'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </Container>
    </div>
  )
}

